import React, { useState, useEffect } from 'react';
import api from '../api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const Settings = () => {
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [utilizzatori, setUtilizzatori] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState('categories');
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#FF6B6B' });

  const colors = [
    // Riga 1 - Rossi e Rosa
    '#FF6B6B', '#EF4444', '#EC4899', '#F43F5E',
    // Riga 2 - Arancioni e Gialli
    '#F97316', '#F59E0B', '#F7B731', '#FFA07A',
    // Riga 3 - Verdi
    '#84CC16', '#22C55E', '#10B981', '#A3CB38',
    // Riga 4 - Azzurri e Blu
    '#06B6D4', '#0EA5E9', '#3B82F6', '#45B7D1',
    // Riga 5 - Viola e Altri
    '#8B5CF6', '#A855F7', '#6366F1', '#4ECDC4',
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, shopsRes, beneficiariesRes, paymentMethodsRes, utilizzatoriRes] =
        await Promise.all([
          api.get('/categories'),
          api.get('/shops'),
          api.get('/beneficiaries'),
          api.get('/payment-methods'),
          api.get('/utilizzatori'),
        ]);

      setCategories(categoriesRes.data);
      setShops(shopsRes.data);
      setBeneficiaries(beneficiariesRes.data);
      setPaymentMethods(paymentMethodsRes.data);
      setUtilizzatori(utilizzatoriRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getEndpoint = () => {
    const endpoints = {
      categories: 'categories',
      shops: 'shops',
      beneficiaries: 'beneficiaries',
      payments: 'payment-methods',
      utilizzatori: 'utilizzatori',
    };
    return endpoints[currentTab];
  };

  const handleOpenDialog = (item = null, tab = currentTab) => {
    setCurrentTab(tab);
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name, color: item.color || '#FF6B6B' });
    } else {
      setEditingItem(null);
      setFormData({ name: '', color: '#FF6B6B' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({ name: '', color: '#FF6B6B' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = getEndpoint();

    try {
      if (editingItem) {
        await api.put(`/${endpoint}/${editingItem.id}`, formData);
        toast.success('Elemento aggiornato con successo');
      } else {
        await api.post(`/${endpoint}`, formData);
        toast.success('Elemento aggiunto con successo');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async () => {
    const endpoint = getEndpoint();
    try {
      await api.delete(`/${endpoint}/${itemToDelete.id}`);
      toast.success('Elemento eliminato con successo');
      fetchData();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const renderItemsList = (items, showColor = false) => (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors bg-white"
        >
          <div className="flex items-center space-x-3">
            {showColor && (
              <div
                className="w-6 h-6 rounded-full"
                style={{ backgroundColor: item.color }}
              />
            )}
            <span className="font-medium text-slate-900">{item.name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenDialog(item, currentTab)}
              className="text-slate-600 hover:text-[#D94E1F] hover:bg-[#D94E1F]/10"
              data-testid={`edit-${item.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setItemToDelete(item);
                setDeleteDialogOpen(true);
              }}
              className="text-slate-600 hover:text-red-600 hover:bg-red-50"
              data-testid={`delete-${item.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      {items.length === 0 && (
        <div className="text-center text-slate-400 py-8">
          Nessun elemento presente. Aggiungi il primo!
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Impostazioni
        </h2>
        <p className="text-slate-600">Gestisci categorie, negozi, beneficiari, metodi di pagamento e utilizzatori</p>
      </div>

      <Tabs defaultValue="categories" onValueChange={setCurrentTab} className="w-full">
        <div className="overflow-x-auto -mx-4 px-4 mb-6">
          <TabsList className="inline-flex min-w-max w-full sm:w-full bg-slate-100 border border-slate-200 h-auto p-1.5 gap-1">
            <TabsTrigger 
              value="categories" 
              data-testid="tab-categories" 
              className="px-3 py-2 text-xs sm:text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              Categorie
            </TabsTrigger>
            <TabsTrigger 
              value="shops" 
              data-testid="tab-shops" 
              className="px-3 py-2 text-xs sm:text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              Negozi
            </TabsTrigger>
            <TabsTrigger 
              value="beneficiaries" 
              data-testid="tab-beneficiaries" 
              className="px-3 py-2 text-xs sm:text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              Beneficiari
            </TabsTrigger>
            <TabsTrigger 
              value="payments" 
              data-testid="tab-payments" 
              className="px-3 py-2 text-xs sm:text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              Pagamenti
            </TabsTrigger>
            <TabsTrigger 
              value="utilizzatori" 
              data-testid="tab-utilizzatori" 
              className="px-3 py-2 text-xs sm:text-sm font-medium whitespace-nowrap bg-white text-slate-700 data-[state=active]:!bg-[#D94E1F] data-[state=active]:!text-white rounded-md"
            >
              Utilizzatori
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => handleOpenDialog(null, 'categories')}
              className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
              data-testid="add-category-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Categoria
            </Button>
          </div>
          {renderItemsList(categories, true)}
        </TabsContent>

        <TabsContent value="shops" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => handleOpenDialog(null, 'shops')}
              className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
              data-testid="add-shop-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Negozio
            </Button>
          </div>
          {renderItemsList(shops)}
        </TabsContent>

        <TabsContent value="beneficiaries" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => handleOpenDialog(null, 'beneficiaries')}
              className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
              data-testid="add-beneficiary-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Beneficiario
            </Button>
          </div>
          {renderItemsList(beneficiaries)}
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => handleOpenDialog(null, 'payments')}
              className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
              data-testid="add-payment-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Metodo
            </Button>
          </div>
          {renderItemsList(paymentMethods)}
        </TabsContent>

        <TabsContent value="utilizzatori" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => handleOpenDialog(null, 'utilizzatori')}
              className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
              data-testid="add-utilizzatore-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Utilizzatore
            </Button>
          </div>
          {renderItemsList(utilizzatori)}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Modifica' : 'Aggiungi Nuovo'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Modifica i dettagli' : 'Inserisci i dettagli'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2"
                  required
                  data-testid="item-name-input"
                />
              </div>

              {currentTab === 'categories' && (
                <div>
                  <Label>Colore *</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {colors.map((color) => (
                      <div
                        key={color}
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-8 h-8 rounded-full transition-all cursor-pointer border-2 ${
                          formData.color === color
                            ? 'ring-2 ring-offset-2 ring-slate-900 scale-110 border-slate-900'
                            : 'hover:scale-105 border-slate-200'
                        }`}
                        style={{ backgroundColor: color }}
                        data-testid={`color-${color}`}
                        role="button"
                        tabIndex={0}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                    <span className="text-sm text-slate-500">Personalizzato:</span>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer border-2 border-slate-200"
                    />
                    <Input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder="#FF6B6B"
                      className="w-24 h-8 text-xs font-mono"
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="cancel-button">
                Annulla
              </Button>
              <Button
                type="submit"
                className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
                data-testid="save-button"
              >
                {editingItem ? 'Salva' : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata. L'elemento verrà eliminato definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;
